// @flow
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";

import { loadModularUI, reloadModel } from "./ModularUIActions";

import Href from "../../models/href/Href";
import { getDisplayName } from "../../react/utils";

import { useUrl } from "./ModularUIUtils";
import { useModularUI } from "../../hooks/useModularUI";
import { useModularUIBasic } from "../../hooks/useModularUIBasic";

import { useLocale } from "../../hooks/useI18n";

import FormModel from "../../models/form/FormModel";

import type { ComponentType, Node } from "react";
import type { Location } from "react-router";
import type { ModularUIModel } from "../../models/types";
import type { ModularUIOptions } from "./types";

export type InjectedProps = {
  +modelKey: string,
  +location: Location,
  +status: string | null,
  +lastModification: number | null,
  +hasModel: boolean,
  +locale: string,
  +fetchModularUI: (href: string | Href, fetchOptions: Object) => void,
  +reloadModel: (model: ModularUIModel, options: Object) => void,
  +data?: ?ModularUIModel,
};

const patchUpdateModelOption = (oldOptions: Object) => {
  if (
    "updateModel" in oldOptions &&
    oldOptions.updateModel instanceof FormModel
  ) {
    return {
      ...oldOptions,
      updateHandler: (newModel: ModularUIModel): ModularUIModel => {
        if (newModel instanceof FormModel) {
          // $FLowFixMe
          const clonedModel: FormModel = oldOptions.updateModel.clone();
          clonedModel.update(newModel);
          return clonedModel;
        }
        return newModel;
      },
    };
  }

  return oldOptions;
};

/**
 * @deprecated - will be removed in the future, use the equivalent hook 'useModularUI' or 'useModularUIBasic' instead
 */
const modularUIConnector = (
  name: string,
  resource: string | Function,
  // $FlowIssue
  options: ModularUIOptions = {},
): ((Component: ComponentType<any>) => (props: any) => Node) => {
  const {
    propName = "data",
    removeOnUnmount = false,
    ...otherOptions
  } = options;

  return (Component: ComponentType<any>) => {
    const WrappedComponent = (props: any) => {
      const finalRemoveOnUnmount = props.removeOnUnmount ?? removeOnUnmount;

      const dispatch = useDispatch();
      const location = useLocation();
      const locale = useLocale();
      const url = useUrl(resource, { location, ...props });

      // $FlowFixMe
      const model = useModularUIBasic<ModularUIModel>(name, url, options);

      const modelEntry = useModularUI(name, url, {
        ...otherOptions,
        removeOnUnmount: finalRemoveOnUnmount,
      });
      const modelKey = model?.connectKey ?? name;

      const handleFetch = (href: string | Href, fetchOptions: Object) => {
        dispatch(
          loadModularUI(modelKey, href, patchUpdateModelOption(fetchOptions)),
        );
      };

      const handleManualReload = (m: ModularUIModel, o: Object) => {
        dispatch(reloadModel(m, o));
      };

      const newProps: InjectedProps = {
        modelKey,
        [propName ?? "data"]: model,
        location,
        status: modelEntry?.status ?? null,
        lastModification: modelEntry?.lastModification ?? null,
        hasModel: Boolean(model),
        locale,
        fetchModularUI: handleFetch,
        reloadModel: handleManualReload,
      };

      return <Component {...props} {...newProps} />;
    };

    WrappedComponent.displayName = `BI.modularui(${getDisplayName(Component, name)})`;
    return WrappedComponent;
  };
};

export default modularUIConnector;
